"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getForumToken } from "./PublicAuth";
import { authedFetch, participationHint } from "./PublicEngage";
import { useForumRole } from "./useForumRole";
import { MOD_PATH } from "../_lib/modPath";

// Moderator controls on the public site. The backend already lets an admin's
// forum session call the /mod endpoints; these are the buttons for it. After
// every action the affected ISR pages are revalidated (see /api/revalidate)
// and the route refreshed, so the public page reflects the change at once.
// (Route: src/app/api/forum-revalidate — /api/* bypasses the middleware.)

export async function revalidateForum(tags: string[]): Promise<void> {
  try {
    await fetch("/api/forum-revalidate", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getForumToken() || ""}` },
      body: JSON.stringify({ tags }),
    });
  } catch {
    // best effort — the page catches up on its own ISR window
  }
}

const btn = "fp-inline text-[12px] font-medium text-slate-600 underline-offset-2 hover:text-blue-700 hover:underline disabled:opacity-50";
const danger = "fp-inline text-[12px] font-medium text-slate-600 underline-offset-2 hover:text-red-700 hover:underline disabled:opacity-50";

function ReasonBox({ onConfirm, onCancel, busy }: { onConfirm: (reason: string) => void; onCancel: () => void; busy: boolean }) {
  const [reason, setReason] = useState("");
  return (
    <div className="basis-full rounded-md border border-slate-200 bg-white p-2.5 shadow-sm">
      <label className="fp-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">Reason (kept on record)</label>
      <input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, 500))}
        placeholder="e.g. Client identifiers, recruiting pitch, off-topic"
        className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
      />
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          disabled={busy || reason.trim().length < 3}
          onClick={() => onConfirm(reason.trim())}
          className="inline-flex h-8 items-center rounded-md bg-red-600 px-3 text-[12px] font-semibold text-white hover:bg-red-700 disabled:opacity-60"
        >
          {busy ? "Removing…" : "Remove"}
        </button>
        <button type="button" onClick={onCancel} className={btn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function PublicModBar({
  post,
  categorySlug,
}: {
  post: { id: string; slug: string; isPinned: boolean; isLocked: boolean; isDeleted: boolean };
  categorySlug: string;
}) {
  const { isModerator } = useForumRole();
  const router = useRouter();
  const [pinned, setPinned] = useState(post.isPinned);
  const [locked, setLocked] = useState(post.isLocked);
  const [removed, setRemoved] = useState(post.isDeleted);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!isModerator) return null;

  const tags = [`forum-post-${post.slug}`, `forum-post-${post.id}`, "forum-feed", `forum-cat-${categorySlug}`, "forum-board"];
  const run = async (action: string, body?: object, after?: () => void) => {
    setBusy(action);
    setError(null);
    try {
      await authedFetch(`/mod/posts/${post.id}/${action}`, { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) });
      after?.();
      await revalidateForum(tags);
      router.refresh();
    } catch (e: any) {
      setError(participationHint(e) || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2">
      <span className="fp-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700">Moderator</span>
      <button type="button" disabled={!!busy} onClick={() => run(pinned ? "unpin" : "pin", undefined, () => setPinned(!pinned))} className={btn}>
        {busy === "pin" || busy === "unpin" ? "…" : pinned ? "Unpin" : "Pin"}
      </button>
      <button type="button" disabled={!!busy} onClick={() => run(locked ? "unlock" : "lock", undefined, () => setLocked(!locked))} className={btn}>
        {busy === "lock" || busy === "unlock" ? "…" : locked ? "Unlock" : "Lock"}
      </button>
      {removed ? (
        <button type="button" disabled={!!busy} onClick={() => run("restore", undefined, () => setRemoved(false))} className={btn}>
          {busy === "restore" ? "…" : "Restore"}
        </button>
      ) : (
        <button type="button" disabled={!!busy} onClick={() => setAsking((a) => !a)} className={danger}>
          Remove
        </button>
      )}
      <Link href={MOD_PATH} className={`${btn} ml-auto`}>
        Mod queue →
      </Link>
      {error && <span className="basis-full text-[12px] text-red-700">{error}</span>}
      {asking && (
        <ReasonBox
          busy={busy === "remove"}
          onCancel={() => setAsking(false)}
          onConfirm={(reason) => run("remove", { reason }, () => { setRemoved(true); setAsking(false); })}
        />
      )}
    </div>
  );
}

export function PublicModComment({ commentId, removed: initialRemoved, postId }: { commentId: string; removed: boolean; postId: string }) {
  const { isModerator } = useForumRole();
  const router = useRouter();
  const [removed, setRemoved] = useState(initialRemoved);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!isModerator) return null;

  const run = async (action: "remove" | "restore", reason?: string) => {
    setBusy(true);
    setError(null);
    try {
      await authedFetch(`/mod/comments/${commentId}/${action}`, { method: "POST", ...(reason ? { body: JSON.stringify({ reason }) } : {}) });
      setRemoved(action === "remove");
      setAsking(false);
      await revalidateForum([`forum-comments-${postId}`, `forum-post-${postId}`]);
      router.refresh();
    } catch (e: any) {
      setError(participationHint(e) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {removed ? (
        <button type="button" disabled={busy} onClick={() => run("restore")} className={btn}>
          {busy ? "…" : "Restore (mod)"}
        </button>
      ) : (
        <button type="button" disabled={busy} onClick={() => setAsking((a) => !a)} className={danger}>
          Remove (mod)
        </button>
      )}
      {error && <span className="basis-full text-[12px] text-red-700">{error}</span>}
      {asking && <ReasonBox busy={busy} onCancel={() => setAsking(false)} onConfirm={(reason) => run("remove", reason)} />}
    </>
  );
}
